import { useGetBankBalancesQuery } from "store/apis/bankConnections";
import { pluralize } from "utils/pluralize";
import { BankConnectModal } from "./ChartOfAccounts/BankConnectModal";
import Async from "./DesignSystem/AsyncComponents/Async";
import { Accordion } from "./DesignSystem/Accordion/Accordion";
import { useModal } from "hooks/useModal";
import { useBankConnect } from "hooks/useBankConnect";
import { Button } from "./DesignSystem/Button/Button";
import { PermissionBasedUI } from "./PermissionBasedUI/PermissionBasedUI";
import { BlockedButton } from "./BlockedButton/BlockedButton";
import {
  CONNECT_BANK_ACCOUNT,
  CLASSIFICATION_FEATURE_ACCESS,
} from "constants/subscriptionPermissionFeatures";
import { useEffect, useMemo } from "react";
import { useFormikContext } from "formik";
import classNames from "classnames";
import { formatDate } from "utils/formatDate";
import { BANK_ACCOUNT_STATUS } from "constants/dataSource";
import { DISPLAY_DATE } from "constants/date";
import dayjs from "dayjs";
import { Tag } from "./DesignSystem/Tag/Tag";
import { BankBalances } from "types/Models/banks";

type BankCardAccount = {
  nickname: string;
  mask: string;
  status?: string;
  closed_on?: string | null;
};

export const TaskConnectBankCardContent = ({
  account,
  isBankConnected,
  accountBalance,
  isPreview,
}: {
  account: BankCardAccount;
  isBankConnected: boolean;
  accountBalance: number;
  isPreview?: boolean;
}) => (
  <div className="t-w-full t-flex t-justify-between t-items-center">
    <div className="t-space-y-1">
      <div
        className={classNames("t-text-body-sm t-flex t-gap-1", {
          "t-text-red-70": !isBankConnected,
          "t-text-text-60": isBankConnected,
          "t-text-text-30": isPreview,
        })}
      >
        {account.nickname}{" "}
        {account.status === BANK_ACCOUNT_STATUS.CLOSED && account.closed_on && (
          <Tag tagType="gray" rounded icon={false}>
            Closed as of {dayjs(account.closed_on).format(DISPLAY_DATE)}
          </Tag>
        )}
      </div>
      <div className="t-text-text-30 t-text-body-sm">•••• {account.mask}</div>
    </div>
    {Boolean(accountBalance) && (
      <div className="t-text-text-60 t-text-subtext-sm">${accountBalance}</div>
    )}
  </div>
);

export const TaskConnectBankCardTrigger = ({
  totalBankConnected,
  name,
  totalBankDisconnected,
  totalBankBalance,
  date,
  disconnectedDate,
  accountsCount,
  isPreview,
}: {
  totalBankConnected: number;
  name: string;
  totalBankDisconnected: number;
  totalBankBalance: number;
  date: string;
  disconnectedDate?: string;
  accountsCount: number;
  isPreview?: boolean;
}) => {
  const dateLabel = disconnectedDate
    ? `disconnected on ${dayjs(disconnectedDate).format(DISPLAY_DATE)}`
    : `as of ${formatDate(date)}`;

  return (
    <div className="t-flex t-justify-between t-items-center t-w-full">
      <div className="t-flex t-flex-col t-gap-1">
        <div className="t-text-text-60 t-text-subtext-sm">{name}</div>
        {totalBankConnected > 0 && (
          <div
            className={classNames("t-text-body-sm t-lowercase", {
              "t-text-dark_green": !isPreview,
              "t-text-text-30": isPreview,
            })}
          >
            {pluralize(
              totalBankConnected,
              "account connected",
              "accounts connected"
            )}
          </div>
        )}
        {totalBankDisconnected > 0 && (
          <div
            className={classNames("t-text-body-sm t-lowercase", {
              "t-text-red-70": !isPreview,
              "t-text-text-30": isPreview,
            })}
          >
            {pluralize(
              accountsCount,
              "account disconnected",
              "accounts disconnected"
            )}
          </div>
        )}
      </div>
      <div className="t-flex t-gap-4 t-mr-3 t-items-center t-normal-case">
        <div className="t-flex t-flex-col t-items-end t-gap-1.5">
          <div className="t-text-text-60 t-text-subtext-sm">
            {totalBankBalance ? `$${totalBankBalance}` : "Data not found"}
          </div>
          <div className="t-text-text-30 t-text-body-sm">{dateLabel}</div>
        </div>
        <div className="t-text-neutral-10">|</div>
      </div>
    </div>
  );
};

export const TaskConnectBank = ({
  groupId,
  entityId,
  openAccordionByDefault = true,
  date,
}: {
  groupId: string;
  entityId: string;
  openAccordionByDefault?: boolean;
  date: string;
}) => {
  const {
    data: bankBalances = [],
    isLoading,
    isSuccess,
  } = useGetBankBalancesQuery({ entityId, date }, { skip: !entityId || !date });

  const totalBalance = parseFloat(
    bankBalances
      .filter((b) => !b.last_disconnected_at)
      .reduce((acc, b) => acc + (b.current_balance || 0), 0)
      .toFixed(2)
  );

  const { setFieldValue } = useFormikContext();

  useEffect(() => {
    setFieldValue("admin_calculated_total_cash_balance", `$ ${totalBalance}`);
  }, [totalBalance, setFieldValue]);

  const banksAccountsGroupByBank = bankBalances.reduce(
    (acc: { [key: string]: BankBalances[] }, curr) => {
      const bankName = curr.bank_brand_name;
      if (!acc[bankName]) {
        acc[bankName] = [];
      }
      acc[bankName].push(curr);
      return acc;
    },
    {}
  );

  const connectBank = useModal();

  const { onConnect } = useBankConnect();

  return (
    <Async.Root
      {...{
        isLoading,
        isSuccess,
        isEmpty: bankBalances.length === 0,
      }}
    >
      <Async.Empty>
        <PermissionBasedUI
          align="start"
          feature={CONNECT_BANK_ACCOUNT}
          blockedUI={
            <BlockedButton size="small">Connect account</BlockedButton>
          }
        >
          <Button
            type="button"
            onClick={connectBank.open}
            customType="primary_outlined"
            size="small"
          >
            Connect account
          </Button>
        </PermissionBasedUI>
      </Async.Empty>
      <Async.Success>
        <Accordion.Root
          defaultValue={
            openAccordionByDefault ? Object.keys(banksAccountsGroupByBank) : []
          }
          type="multiple"
          className="t-flex t-flex-col t-gap-4 t-w-full"
        >
          {Object.entries(banksAccountsGroupByBank).map(([name, accounts]) => {
            const connectedAccounts = accounts.filter(
              (a) => !a.last_disconnected_at
            );
            const disconnectedAccounts = accounts.filter(
              (a) => a.last_disconnected_at
            );

            const totalBankConnected = connectedAccounts.length;
            const totalBankDisconnected = disconnectedAccounts.length;

            const totalBankBalance = parseFloat(
              accounts
                .reduce((acc, a) => acc + (a.current_balance || 0), 0)
                .toFixed(2)
            );

            const disconnectedDate =
              totalBankConnected === 0
                ? disconnectedAccounts.reduce(
                    (latest, a) =>
                      a.last_disconnected_at &&
                      dayjs(a.last_disconnected_at).isAfter(dayjs(latest))
                        ? a.last_disconnected_at
                        : latest,
                    disconnectedAccounts[0]?.last_disconnected_at || ""
                  )
                : undefined;

            return (
              <Accordion.Item value={name} key={name}>
                <Accordion.Trigger>
                  <TaskConnectBankCardTrigger
                    totalBankConnected={totalBankConnected}
                    name={name}
                    totalBankDisconnected={totalBankDisconnected}
                    totalBankBalance={totalBankBalance}
                    date={date}
                    disconnectedDate={disconnectedDate}
                    accountsCount={accounts.length}
                  />
                </Accordion.Trigger>
                <Accordion.Content>
                  <div className="t-flex t-flex-col t-gap-3">
                    {accounts.map((account) => (
                      <TaskConnectBankCardContent
                        key={account.account_id}
                        account={account}
                        isBankConnected={!account.last_disconnected_at}
                        accountBalance={account.current_balance || 0}
                      />
                    ))}
                  </div>
                </Accordion.Content>
              </Accordion.Item>
            );
          })}
          <div>
            <PermissionBasedUI
              align="start"
              feature={CONNECT_BANK_ACCOUNT}
              blockedUI={
                <BlockedButton size="small" type="button">
                  Connect more
                </BlockedButton>
              }
            >
              <Button
                onClick={connectBank.open}
                customType="primary_outlined"
                size="small"
                type="button"
              >
                Connect more
              </Button>
            </PermissionBasedUI>
          </div>
        </Accordion.Root>
      </Async.Success>
      <BankConnectModal
        isOpen={connectBank.isOpen}
        close={connectBank.close}
        onConnect={onConnect}
        entityIdFromParent={entityId}
      />
    </Async.Root>
  );
};
